import React, { createContext, useState, useEffect, useContext } from "react";
import axios from "axios";
import config from '../config';
import { useNavigate } from "react-router-dom";

interface Customer {
    id: string,
    document: string | null,
    name: string,
    externalId: string,
    externalType: string
}
interface Order {
    id: string,
    code: string,
    dateApproved: Date | null,
    externalId: string,
    externalType: string,
    customer: Customer,
    imported: boolean
}
interface Culture {
    id: string,
    name: string,
    externalId: string,
    externalType: number
}
interface Sector {
    id: string,
    name: string,
    nickname: string,
    externalId: string,
    externalType: number
}
interface Farm {
    id: string,
    name: string,
    externalId: string,
    externalType: number
}
interface Service {
    id: string;
    name: string;
    externalId: string;
    externalType: number;
}
interface Samples {
    id: string,
    externalId: string,
    culture: Culture,
    sector: Sector,
    farm: Farm,
    service: Service,
    plot: string,
    depth: string | null,
    externalType: number,
    description: string
}
interface OrdersContextType {
    orders: Order[];
    order: Order | null;
    samples: Samples[];
    token: string | null;
    error: string | null;
    loading: boolean;
    checkAuth: () => Promise<boolean>;
    setToken: (token: string | null) => void;
    setError: (erro: string | null) => void;
    fetchOrderById: (id: string) => Promise<void>;
    importFile: (file: File, id: string) => Promise<void>;
    fetchOrders: () => Promise<void>;
    confirmImport: (id: string) => Promise<void>;
    exportIdentification: (label: string) => Promise<void>;
    exportConsolidated: (file: File) => Promise<void>;
}

export const OrdersContext = createContext<OrdersContextType | null>(null);

export const OrdersProvider = ({ children }: { children: React.ReactNode }) => {
    const [orders, setOrders] = useState<Order[]>([]);
    const [order, setOrder] = useState<Order | null>(null);
    const [samples, setSamples] = useState<Samples[]>([]);
    const [token, setToken] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    // busca todas as orders
    const fetchOrders = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${config.backendUrl}/api/orders`);
            setOrders(response.data.orders);
        } catch (error) {
            console.error("Erro ao buscar pedidos", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrderById = async (id: string): Promise<void> => {
        try {
            setLoading(true);
            const response = await axios.get(`${config.backendUrl}/api/orders/external/${id}`);
            setOrder(response.data);
            setError(null);
        } catch (error: any) {
            let errorMessage = "Erro ao buscar o pedido. Tente novamente.";

            if (error.response) {
                if (error.response.status === 404) {
                    errorMessage = "Pedido não encontrado.";
                } else {
                    errorMessage = error.response.data?.message || "Erro ao buscar pedido.";
                }
            } else if (error.message) {
                errorMessage = error.message;
            }
            setError(errorMessage);
            setOrder(null); // Limpar o estado de pedido em caso de erro
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    };


    // Verifica se o usuário está autenticado
    const checkAuth = async (): Promise<boolean> => {
        if (!token) return false;

        try {
            const response = await axios.post(`${config.apiUrl}/validate`, {
                token, // Enviando token no body
            });

            return response.status === 200;
        } catch (error) {
            setToken(null);
            navigate("/login"); // Redireciona para login caso não esteja autenticado
            return false;
        }
    };

    const importFile = async (file: File, id: string) => {
        try {
            const formData = new FormData();
            formData.append("file", file);

            const response = await axios.post(
                `${config.backendUrl}/api/samples/import/${id}`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            setSamples(response.data);
        } catch (error: any) {
            let errorMessage = "Erro ao importar arquivo. Tente novamente.";
            if (error.response) {
                errorMessage = error.response.data?.message || errorMessage;
            } else if (error.message) {
                errorMessage = error.message;
            }
            throw new Error(errorMessage);
        }
    };

    const confirmImport = async (id: string): Promise<void> => {
        try {
            setLoading(true);
            const response = await axios.post(
                `${config.backendUrl}/api/samples/send-identification/${id}`,
                {},
                { timeout: 300000 }
            );

            if (response.status === 202) {
                console.log("Processo iniciado, monitorando status...");
                await checkStatus(response.data.order.id);
            } else {
                console.log("Erro inesperado:", response);
            }
        } catch (error: any) {
            let errorMessage = "Erro ao confirmar importação. Tente novamente.";
            if (error.response) {
                errorMessage = error.response.data?.message || errorMessage;
            } else if (error.message) {
                errorMessage = error.message;
            }
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const checkStatus = async (id: string) => {
        while (true) {
            const statusResponse = await axios.get(`${config.backendUrl}/api/orders/${id}`);
            if (statusResponse.data.imported == true) {
                console.log("Processamento concluído!");
                break;
            }
            await new Promise((resolve) => setTimeout(resolve, 5000)); // Espera 5s antes de verificar novamente
        }
    };

    // Indentifica e gera o excel 
    const exportIdentification = async (label: string): Promise<void> => {
        try {
            setLoading(true);
    
            const response = await axios.post(
                `${config.backendUrl}/api/samples/export-identification`,
                { label },
                { responseType: "blob" }
            );
    
            // Extrai o nome do arquivo do header Content-Disposition
            let filename = label + ".xlsx"; // fallback
            const contentDisposition = response.headers["content-disposition"];
            if (contentDisposition) {
                const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
                if (utf8Match && utf8Match[1]) {
                    filename = decodeURIComponent(utf8Match[1]);
                } else {
                    const match = contentDisposition.match(/filename="?([^"]+)"?/i);
                    if (match && match[1]) filename = match[1];
                }
            }
    
            // Cria link temporário para baixar o arquivo
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
    
        } catch (error: any) {
            let errorMessage = "Erro ao exportar identificação. Tente novamente.";
            if (error.response) {
                errorMessage = error.response.data?.message || errorMessage;
            } else if (error.message) {
                errorMessage = error.message;
            }
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    };
    
    const exportConsolidated = async (file: File): Promise<void> => {
        try {
            setLoading(true);
            setError(null);

            const formData = new FormData();
            formData.append("file", file);

            const response = await axios.post(
                `${config.backendUrl}/api/samples/export-consolidated`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                    responseType: "blob",
                }
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            
            const contentDisposition = response.headers["content-disposition"];
            let filename = "consolidado.xlsx";
            if (contentDisposition) {
                const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
                if (utf8Match && utf8Match[1]) {
                    filename = decodeURIComponent(utf8Match[1]);
                } else {
                    const match = contentDisposition.match(/filename="?([^"]+)"?/i);
                    if (match && match[1]) filename = match[1];
                }
            }

            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

        } catch (error: any) {
            let errorMessage = "Erro ao exportar o arquivo consolidado.";
            if (error.response) {
                errorMessage = error.response.data?.message || errorMessage;
            }
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    };
    
    return (
        <OrdersContext.Provider value={{ orders, order, samples, token, error, loading, checkAuth, setToken, setError, fetchOrderById, importFile, fetchOrders, confirmImport, exportIdentification, exportConsolidated }}>
            {children}
        </OrdersContext.Provider>
    );
};

export const useOrders = () => {
    const context = useContext(OrdersContext);
    if (!context) {
        throw new Error("useOrders deve ser usado dentro de um OrdersProvider");
    }
    return context;
};